import { supabase } from '../../sistema/services/supabaseClient';

// Tipos
export interface TagCatalogo {
  id: string;
  nome: string;
  uso_count: number;
}

export interface FotoTag {
  id: string;
  foto_id: string;
  tag_id: string;
  criado_em: string;
}

export interface FotoComTags {
  foto_id: string;
  tags: string[];
  tag_ids: string[];
}

// Buscar tags disponíveis de uma obra (para autocomplete)
export async function buscarTagsObra(
  obraId: string,
  busca?: string,
  limite: number = 20
): Promise<TagCatalogo[]> {
  const { data, error } = await supabase.rpc('buscar_tags_obra', {
    p_obra_id: obraId,
    p_busca: busca || null,
    p_limite: limite
  });

  if (error) {
    console.error('Erro ao buscar tags da obra:', error);
    throw error;
  }

  return data || [];
}

// Buscar tags de uma foto específica
export async function buscarTagsFoto(fotoId: string): Promise<TagCatalogo[]> {
  const { data, error } = await supabase
    .from('foto_tags')
    .select(`
      id,
      tag_id,
      foto_tags_catalogo (
        id,
        nome
      )
    `)
    .eq('foto_id', fotoId);

  if (error) {
    console.error('Erro ao buscar tags da foto:', error);
    throw error;
  }

  return (data || []).map((item: any) => ({
    id: item.foto_tags_catalogo.id,
    nome: item.foto_tags_catalogo.nome,
    uso_count: 0
  }));
}

// Adicionar tag a uma foto (cria no catálogo se não existir)
export async function adicionarTagFoto(
  fotoId: string,
  tagNome: string,
  obraId: string,
  usuarioId?: string
): Promise<string> {
  const { data, error } = await supabase.rpc('adicionar_tag_foto', {
    p_foto_id: fotoId,
    p_tag_nome: tagNome,
    p_obra_id: obraId,
    p_usuario_id: usuarioId || null
  });

  if (error) {
    console.error('Erro ao adicionar tag:', error);
    throw error;
  }

  return data;
}

// Remover tag de uma foto
export async function removerTagFoto(
  fotoId: string,
  tagId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('remover_tag_foto', {
    p_foto_id: fotoId,
    p_tag_id: tagId
  });

  if (error) {
    console.error('Erro ao remover tag:', error);
    throw error;
  }

  return data;
}

// Buscar fotos por tag (útil para pesquisa)
export async function buscarFotosPorTag(
  obraId: string,
  tagId: string
): Promise<string[]> {
  const { data, error } = await supabase
    .from('fotos_com_tags')
    .select('foto_id')
    .eq('obra_id', obraId)
    .contains('tag_ids', [tagId]);

  if (error) {
    console.error('Erro ao buscar fotos por tag:', error);
    throw error;
  }

  return (data || []).map((item: any) => item.foto_id);
}

// Buscar fotos por nome de tag (útil para pesquisa textual)
export async function buscarFotosPorTagNome(
  obraId: string,
  tagNome: string
): Promise<string[]> {
  // Primeiro encontra a tag
  const { data: tagData, error: tagError } = await supabase
    .from('foto_tags_catalogo')
    .select('id')
    .eq('obra_id', obraId)
    .ilike('nome', `%${tagNome}%`);

  if (tagError) {
    console.error('Erro ao buscar tag:', tagError);
    throw tagError;
  }

  if (!tagData || tagData.length === 0) {
    return [];
  }

  const tagIds = tagData.map((t: any) => t.id);

  // Busca fotos com essas tags
  const { data, error } = await supabase
    .from('foto_tags')
    .select('foto_id')
    .in('tag_id', tagIds);

  if (error) {
    console.error('Erro ao buscar fotos:', error);
    throw error;
  }

  // Remove duplicatas
  return [...new Set((data || []).map((item: any) => item.foto_id))];
}

// Buscar múltiplas fotos com suas tags (batch)
export async function buscarTagsMultiplasFotos(
  fotoIds: string[]
): Promise<Map<string, TagCatalogo[]>> {
  if (fotoIds.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('foto_tags')
    .select(`
      foto_id,
      foto_tags_catalogo (
        id,
        nome
      )
    `)
    .in('foto_id', fotoIds);

  if (error) {
    console.error('Erro ao buscar tags das fotos:', error);
    throw error;
  }

  const result = new Map<string, TagCatalogo[]>();

  for (const item of data || []) {
    const fotoId = item.foto_id;
    const tag: TagCatalogo = {
      id: (item as any).foto_tags_catalogo.id,
      nome: (item as any).foto_tags_catalogo.nome,
      uso_count: 0
    };

    if (!result.has(fotoId)) {
      result.set(fotoId, []);
    }
    result.get(fotoId)!.push(tag);
  }

  return result;
}

// Excluir tag do catálogo (remove também todas as associações com fotos)
export async function excluirTagCatalogo(tagId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('excluir_tag_catalogo', {
    p_tag_id: tagId
  });

  if (error) {
    console.error('Erro ao excluir tag:', error);
    throw error;
  }

  return data;
}
